
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { colors } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (item: string) => {
    const itemId = item.toLowerCase().replace(' ', '-');
    const currentPath = window.location.pathname;
    
    // If clicking "Home", always go to home page
    if (itemId === "home") {
      window.location.href = "/";
      setIsOpen(false);
      return;
    }
    
    // For main sections that exist on the home page
    const homeSections = ["services", "about-us", "our-mission", "contact"];
    
    if (homeSections.includes(itemId)) {
      if (currentPath !== "/") {
        // Navigate to home page with hash
        window.location.href = `/#${itemId}`;
      } else {
        // Already on home page, just scroll to section
        const element = document.getElementById(itemId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    
    setIsOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 border-b transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-lg shadow-lg' : 'backdrop-blur-md'
      }`}
      style={{ 
        backgroundColor: `${colors.background}95`,
        borderColor: `${colors.border}60`
      }}
    >
      <div className="w-full px-4 md:max-w-6xl md:mx-auto">
        <div className="flex items-center justify-between h-16">
          <motion.a 
            href="/" 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Logo />
          </motion.a>
          
          <div className="md:hidden flex items-center gap-2">
            {/* <ThemeToggle /> */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="border transition-colors hover:scale-105"
              style={{ 
                color: colors.foreground,
                borderColor: `${colors.border}60`,
                backgroundColor: `${colors.card}40`
              }}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>

          <div className="hidden md:flex items-center gap-6">
            {["Home", "Services", "About Us", "Our Mission", "Contact"].map((item) => (
              <motion.button
                key={item}
                onClick={() => handleNavigation(item)}
                className="transition-colors relative font-semibold"
                style={{ color: colors.foreground }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item}
                <motion.div
                  className="absolute -bottom-1 left-0 w-full h-0.5"
                  style={{ backgroundColor: colors.primary }}
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              </motion.button>
            ))}
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="md:hidden py-4 backdrop-blur-md border-t"
              style={{ 
                backgroundColor: `${colors.background}95`,
                borderColor: `${colors.border}40`
              }}
            >
              <div className="flex flex-col gap-4">
                {["Home", "Services", "About Us", "Our Mission", "Contact"].map((item) => (
                  <motion.button
                    key={item}
                    onClick={() => handleNavigation(item)}
                    className="transition-colors text-left"
                    style={{ color: colors.foreground }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};
